import React, {createElement} from 'react';
import moment from 'moment';
import {idealRanges, idealRangesFormatted, fillStars, getIdealRange, getMinMax} from 'helpers/dataHelpers';
import ConstraintAlertField from './../../components/constraintAlertField.jsx';
import {phosphateLevelsNames} from 'helpers/constants';
import {languages} from 'helpers/languageHelper';


export const IDEAL_TESTS = 4;
const LEAVE_BLANK = 'LEAVE_BLANK';

export default function generateDetailedView(intl, dealer, branch, pool, products, columns = [],
                                             formatMessage, historyView, separateIdealsColumn = true, fillBlank = false, plainText = false) {
  let tableRows = [];
  let numAnalyses = columns.length;
  let idealRanges = idealRangesFormatted(formatMessage);

  if (dealer.poolScores) {
    insertPoolScores(separateIdealsColumn, columns, tableRows, fillBlank, formatMessage, plainText);
  }

  for (let key in idealRanges) {
    if(key) {
      let [analyses, numNotTested, numNos] = calculateRow(intl, dealer, pool, products, columns, key, dealer);

      let ideal = getIdealRange(key, columns[0], pool, products, dealer.country);

      if ((numNotTested < numAnalyses && numNos < numAnalyses || idealRanges[key].required) &&
      (!idealRanges[key].condition || idealRanges[key].condition(columns[0], pool, products, dealer)) || historyView) {
        let title = idealRanges[key].title;

        if (typeof title === 'function') {
          title = title(dealer, branch, pool, formatMessage, !!analyses.filter(x => !!x.optimizerProductID).length);
        }

        if (!separateIdealsColumn && ideal) {
          title = <div>{title}<span className='pull-right'>({ideal})</span></div>;
        }

        let separator = idealRanges[key].separator ? 'tablerow-separator' : null;

        if (idealRanges[key].separator && typeof idealRanges[key].separator === 'function') {
          separator = idealRanges[key].separator(null, pool) ? 'tablerow-separator' : null;
        }


        tableRows.push(<tr key={'row' + key}
                         className={separator}>
          <td>{title}</td>
          {separateIdealsColumn ? <td>{ideal}</td> : null}
          {analyses}
          {fillBlank ? generateBlankCells(numAnalyses) : null}
        </tr>);
      }
    }
  }

  return tableRows;
}

export function generateBlankCells(numAnalyses, ideal = IDEAL_TESTS, type = 'td') {
  return new Array(ideal - numAnalyses).fill(undefined).map((x, i) => createElement(type, {key: i}));
}

function insertPoolScores(separateIdealsColumn = true, columns = [], tableRows = [], fillBlank = false, formatMessage, plainText = false) {
  tableRows.push(<tr key={'row' + 'swimmer-comfort'}>
    <td>{formatMessage({id: 'general.comfort'})}</td>
    {separateIdealsColumn ? <td/> : null}
    {columns.map((x, i) => {
      if (x.season !== 'in' || x.chlorineDemandOnly) {
        return <td/>;
      }
      return <td key={'swimmerComfort' + i}>{fillStars(x.comfortScore, plainText)}</td>;
    })}
    {fillBlank ? generateBlankCells(columns.length) : null}
  </tr>);

  tableRows.push(<tr key={'row' + 'water-clarity'}>
    <td>{formatMessage({id: 'general.clarity'})}</td>
    {separateIdealsColumn ? <td/> : null}
    {columns.map((x, i) => {
      if (x.season !== 'in' || x.chlorineDemandOnly) {
        return <td/>;
      }
      return <td key={'waterClarity' + i}>{fillStars(x.clarityScore, plainText)}</td>;
    })}
    {fillBlank ? generateBlankCells(columns.length) : null}
  </tr>);

  tableRows.push(<tr key={'row' + 'surface-protection'}>
    <td>{formatMessage({id: 'general.protection'})}</td>
    {separateIdealsColumn ? <td/> : null}
    {columns.map((x, i) => {
      if (x.season !== 'in' || x.chlorineDemandOnly) {
        return <td/>;
      }
      return <td key={'surface-protection' + i}>{fillStars(x.protectionScore, plainText)}</td>;
    })}
    {fillBlank ? generateBlankCells(columns.length) : null}
  </tr>);
}

function calculateRow(intl, dealer, pool, products, columns = [], key = '') {
  let numNotTested = 0;
  let numNos = 0;

  let analyses = columns.map((analysis, index) => {
    let val;

    [val, numNotTested, numNos] = formatCell(intl, key, analysis, dealer, numNotTested, numNos);

    let [min, max] = getMinMax(key, analysis, pool, products, dealer.country);

    return (
      <td key={index + '.' + key}>
        <ConstraintAlertField
          value={val}
          displayOptions={idealRanges[key].displayOptions}
          min={min}
          max={max}
          exact={idealRanges[key].exact}/>
      </td>
    );
  });

  return [analyses, numNotTested, numNos];
}

function specialEvents(formatMessage, analysis, blank = false, disableFrontEndTranslations = false) {
  let output = [];

  if (!!analysis.severeWeatherProduct) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.events.severe-weather'}) : 'detailed-report.events.severe-weather');
  }
  if (!!analysis.prePartyProduct) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.events.pre-party'}) : 'detailed-report.events.pre-party');
  }
  if (!!analysis.postPartyProduct) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.events.post-party'}) : 'detailed-report.events.post-party');
  }

  let val = output.join(', ');

  if (!val && analysis.id) {
    val = LEAVE_BLANK;
  }

  if (blank) {
    return trueBlank(val);
  }

  return val;
}


function algaeProblems(formatMessage, analysis, blank = false, disableFrontEndTranslations = false) {
  let output = [];

  if (analysis.swampy) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'general.swampy'}) : 'general.swampy');
  }
  if (analysis.greenAlgae) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.algae.green'}) : 'detailed-report.problems.algae.green');
  }
  if (analysis.blackAlgae === '1' || analysis.blackAlgae === '2' || analysis.blackAlgae === 'L' || analysis.blackAlgae === 'E') {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.algae.black'}) : 'detailed-report.problems.algae.black');
  }
  if (analysis.mustardAlgae) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.algae.mustard'}) : 'detailed-report.problems.algae.mustard');
  }
  if (analysis.spaAlgae) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.algae.spa'}) : 'detailed-report.problems.algae.spa');
  }

  let algae = output.join(', ');

  if (!algae && analysis.id) {
    algae = LEAVE_BLANK;
  }

  return blank ? trueBlank(algae) : algae;
}

function waterClarityProblems(formatMessage, analysis, blank = false, disableFrontEndTranslations = false) {
  let output = [];

  if (analysis.hazy) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.hazy'}) : 'detailed-report.problems.hazy');
  }
  if (analysis.cloudy) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.cloudy'}) : 'detailed-report.problems.cloudy');
  }
  if (analysis.foaming) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.foaming'}) : 'detailed-report.problems.foaming');
  }

  if (!!analysis.pollen) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.pollen'}) : 'detailed-report.problems.pollen');
  }

  let val = output.join(', ');

  if (!val && analysis.id) {
    val = LEAVE_BLANK;
  }

  if (blank) {
    return trueBlank(val);
  }

  return val;
}

function surfaceProblems(formatMessage, analysis, blank = false, disableFrontEndTranslations = false) {
  let output = [];

  if (analysis.waterBuildup) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.buildup.waterline'}) : 'detailed-report.problems.buildup.waterline');
  }
  if (analysis.scaleBuildup) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.buildup.scale'}) : 'detailed-report.problems.buildup.scale');
  }
  if (analysis.ironStaining) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.staining.iron'}) : 'detailed-report.problems.staining.iron');
  }
  if (analysis.copperStaining === '1' || analysis.copperStaining === '2' || analysis.copperStaining === 1 || analysis.copperStaining === 2) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.staining.copper'}) : 'detailed-report.problems.staining.copper');
  }
  if (analysis.organicStaining) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.staining.organic'}) : 'detailed-report.problems.staining.organic');
  }
  if (analysis.unknownStaining) {
    output.push(!disableFrontEndTranslations ? formatMessage({id: 'detailed-report.problems.staining.unknown'}) : 'detailed-report.problems.staining.unknown');
  }

  let val = output.join(', ');

  return !val && analysis.id && !blank ? LEAVE_BLANK : trueBlank(val);
}

function chlorineDemandProblems(analysis) {
  if ((analysis.cDSample || analysis.cDSample === 0) && (analysis.cDDistStandard || analysis.cDDistStandard === 0)) {
    return `${analysis.cDSample} (${analysis.cDDistStandard})`;
  }
  return null;
}

function branchName(analysis) {
  if (!analysis || !analysis.branchID || analysis.branchID < 1 || !analysis.branchName) {
    return LEAVE_BLANK;
  }
  return analysis.branchName;
}

function formatValue({formatMessage, formatNumber, locale}, key, analysis, val, numNotTested, numNos, overwriteDisplayValueLanguageKey = null) {
  // Format null values
  if (analysis.id && !val && val !== LEAVE_BLANK && val !== 0 && val !== false && key !== 'waterTester') {
    val = formatMessage({id: 'general.not.tested'});
    numNotTested += 1;
  }

  if (val === LEAVE_BLANK) {
    val = '';
  }

  // Format booleans
  if (val === false && analysis.id) {
    val = formatMessage({id: 'general.no'});
    numNos += 1;
  } else if (val === true) {
    val = formatMessage({id: 'general.yes'});
  }

  // Format floats
  if (val && val.toString() === parseFloat(val).toString() && val.toString() !== parseInt(val).toString()) {
    val = formatNumber(val, {minimumFractionDigits: 1, maximumFractionDigits: 1});
  }

  // Format dates
  if (val && (val.toString().match(/Date\([0-9]+\)/) || moment.isMoment(val) && val.isValid())) {
    val = moment(val).locale(languages[locale].time).format('LL');
  }

  if (typeof val === 'string' && overwriteDisplayValueLanguageKey) {
    val = formatMessage({id: overwriteDisplayValueLanguageKey});
  }

  return [val, numNotTested, numNos];
}

export function formatCell({formatMessage, formatNumber, locale}, key, analysis, dealer, numNotTested = 0, numNos = 0, disableFrontEndTranslations = false) {
  let val;
  let overwriteDisplayValueLanguageKey = null;

  if (key === 'algae') {
    val = algaeProblems(formatMessage, analysis, disableFrontEndTranslations);
  } else if (key === 'waterClarity') {
    val = waterClarityProblems(formatMessage, analysis, disableFrontEndTranslations);
  } else if (key === 'surfaceProblems') {
    val = surfaceProblems(formatMessage, analysis, disableFrontEndTranslations);
  } else if (key === 'cDSample') {
    val = chlorineDemandProblems(analysis);
  } else if (key === 'branchName') {
    val = branchName(analysis);
  } else if (key === 'phosphate') {
    if(analysis.phosphateLevel || analysis.phosphateLevel === 0) {
      val = phosphateLevelsNames(formatMessage)[analysis.phosphateLevel];
    }else {
      val = analysis.phosphate;
    }
  } else if (key === 'events') {
    val = specialEvents(formatMessage, analysis, disableFrontEndTranslations);
  } else {
    val = analysis[key];
  }

  // Format keys in dictionaries
  if (idealRanges[key].displayOptions) {
    if(typeof idealRanges[key].displayOptions === 'function') {
      val = idealRanges[key].displayOptions(formatMessage, analysis) || LEAVE_BLANK;
    }else if(idealRanges[key].displayOptions[val]) {
      //Key to lookup  in language dictionary under general section
      overwriteDisplayValueLanguageKey = `general.${key}.${val}`;
      val = idealRanges[key].displayOptions[val];

    }
  }

  return formatValue({formatMessage, formatNumber, locale}, key, analysis, val,
    numNotTested, numNos, overwriteDisplayValueLanguageKey);
}

function trueBlank(value) {
  if (value === null || value === undefined || value === LEAVE_BLANK) {
    return '';
  }
  return value;
}

export function emailSummary({formatMessage, formatNumber, locale}, analysis, pool, products, dealer, branch, disableFrontEndTranslations = false) {
  let data = [];

  for (let key of Object.keys(idealRanges)) {
    let title = idealRanges[key].title;

    if (typeof title === 'function') {
      title = title(dealer, branch, pool, formatMessage, !!analysis.optimizerProductID);
    }

    let ideal = getIdealRange(key, analysis, pool, products, dealer.country, formatNumber);
    let [value, notTested, no, displayValueLanguageKey] = formatCell({formatMessage, formatNumber, locale},
      key, analysis, dealer, 0, 0, disableFrontEndTranslations);

    if ((notTested !== 1 && no !== 1 || idealRanges[key].required) &&
      (!idealRanges[key].condition || idealRanges[key].condition(analysis, pool, products, dealer))) {
      data.push([key, value + '', ideal, title, displayValueLanguageKey]);
    }
  }

  return {
    data,
    events: specialEvents(formatMessage, analysis, true, disableFrontEndTranslations),
    algaeProblems: algaeProblems(formatMessage, analysis, true, disableFrontEndTranslations),
    surfaceProblems: surfaceProblems(formatMessage, analysis, true, disableFrontEndTranslations),
    waterClarityProblems: waterClarityProblems(formatMessage, analysis, true, disableFrontEndTranslations),
    comfortScore: analysis.comfortScore,
    clarityScore: analysis.clarityScore,
    protectionScore: analysis.protectionScore,
    currentDealerLanguage: locale,
  };
}
